/* eslint-disable no-console */
import React, { createContext, useMemo, useState, useEffect, useCallback } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { io, Socket } from 'socket.io-client'
import config from 'constants/config'
import {
  set as setLocalStorage,
  get as getLocalStorage,
  remove as removeLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { authLogout } from 'actions/AuthActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { isLoggedIn } from 'selectors/accountSelectors'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { accountAccessShowLogin } from 'actions/UiActions'

interface SessionSocket extends Socket {
  sessionId?: string
  customerId?: string
}

export const SocketContext = createContext<{
  socket: SessionSocket | null,
  onLogin:() => Promise<{
      loggedIn: boolean;
      firstName?: string;
      lastName?: string;
  }>
    }>({
      socket: null,
      onLogin: () => new Promise(() => {}),
    })

function SocketContextProvider({ children }) {
  const [socket, setSocket] = useState<SessionSocket | null>(null)

  const dispatch = useAppDispatch()

  const userIsLoggedIn = useAppSelector(isLoggedIn)
  const userId = useAppSelector((state: App.State) => state.auth.account?.memberId)
  const accessToken = useAppSelector((state: App.State) => state.auth.accessToken)

  const loginListener = useReduxStateListener<{ loggedIn: boolean; firstName?: string; lastName?: string }>((state) => {
    if (isLoggedIn(state)) {
      return { loggedIn: true, firstName: state.auth.account.givenName, lastName: state.auth.account.surname }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const sessionDebug = (message, ...rest) => {
    if (config.SUPPORT_ASSISTANT_DEBUG) {
      console.log(message)
      console.log(...rest)
    }
  }

  useEffect(() => {
    if (!userIsLoggedIn) {
      sessionDebug(':: !userIsLoggedIn ::', !userIsLoggedIn)
      removeLocalStorage('supportAssistantSessionId')
      setSocket(null)

      loginListener.awaitResult().then(({ loggedIn }) => {
        if (loggedIn) {
          sessionDebug(':: loginListener ::', loggedIn)
          setSocket(null)
        }
      })
    }
    // show login modal if we log out customer by invalidating session
    if (userIsLoggedIn) {
      loginListener.awaitResult().then(({ loggedIn }) => {
        if (!loggedIn) {
          dispatch(accountAccessShowLogin())
        }
      })
    }
  }, [userIsLoggedIn, loginListener, dispatch])

  useEffect(() => {
    const sessionId = getLocalStorage('supportAssistantSessionId')
    sessionDebug(':: socket supportAssistantSessionId ::', !!socket, sessionId)

    if (socket) {
      socket.on('connect', () => {
        const sessionIdEvent = getLocalStorage('supportAssistantSessionId')
        sessionDebug(':: socket connected ::', socket.sessionId, socket.customerId)
        sessionDebug(':: !session && userId ::', sessionId, sessionIdEvent, userId)
      })

      socket.on('session:created', (socket) => {
        const sessionId = socket.sessionId
        const customerId = socket.customerId

        sessionDebug('session:created', sessionId, customerId, customerId === userId)
        if (customerId === userId) {
          setLocalStorage('supportAssistantSessionId', sessionId)
        }
      })

      socket.on('session:expired', () => {
        sessionDebug('session:expired')
        removeLocalStorage('supportAssistantSessionId')
        setSocket(null)
      })

      socket.on('accessToken:invalid', () => {
        sessionDebug('accessToken:invalid')
        dispatch(authLogout())
        setSocket(null) // see if needed here
      })

      return () => {
        socket.off('session:created')
        socket.off('session:expired')
        socket.off('accessToken:invalid')
      }
    }
  }, [dispatch, socket, userId])

  useEffect(() => {
    const sessionId = getLocalStorage('supportAssistantSessionId')

    if (!socket) {
      sessionDebug(':: !socket supportAssistantSessionId ::', sessionId)
      const socketIO = io(config.API_HOST, {
        transports: ['websocket', 'polling'],
        path: '/api/support/chat/socket.io',
        auth: {
          customerId: userId,
          sessionId,
          accessToken,
        },
      })

      socketIO.on('connect_error', () => {
        // revert to classic upgrade
        socketIO.io.opts.transports = ['polling', 'websocket']
      })

      setSocket(socketIO)
      return () => {
        socketIO.disconnect()
      }
    }
  }, [socket, userId, accessToken])

  const onLogin = useCallback(async() => {
    dispatch(accountAccessShowLogin())
    return loginListener.awaitResult().then(({ loggedIn, firstName, lastName }) => {
      if (loggedIn) {
        setSocket(null)
      }
      return {
        loggedIn,
        firstName,
        lastName,
      }
    })
  }, [dispatch, loginListener])

  const contextValue = useMemo(() => {
    return {
      socket,
      onLogin,
    }
  }, [socket, onLogin])

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  )
}

export default SocketContextProvider
